import React from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Menu,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  FolderOpenOutlined,
  MergeCellsOutlined,
  SendOutlined,
  SwapOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import {
  CaretDown,
  ClipboardText,
  CloudArrowUp,
  DotsThree,
} from '@phosphor-icons/react';

import Button from '../../../../components/Button';
import { captureAnalytics } from '../../../../actions/preload';
import { chartClarityTooltips } from '../../../../global/featureFlags';

const trackAction = (action, selectedCount, placement = 'primary') => {
  captureAnalytics('uploaded_records_action_clicked', {
    action,
    placement,
    selected_count: selectedCount,
  });
};

const UploadedActionBar = ({
  selectedData,
  title,
  viewingArchivedRecords,
  canEdit,
  maxBulkSendRecords,
  autoUploadToEMR,
  createPatientChart,
  canCreateTask,
  referralManagement,
  autoUploadEmr,
  onUpload,
  onSend,
  onEdit,
  onSendToChart,
  onCreateChart,
  onCreateTask,
  onMoveToReferralManagement,
  onMerge,
  onMergeAndArchive,
  onArchive,
  onUnarchive,
  onCreateCopy,
}) => {
  const selectedCount = selectedData.length;
  const selectedRecord = selectedCount === 1 ? selectedData[0] : null;
  const hasSelection = selectedCount > 0;
  const hasChartActions = autoUploadToEMR || createPatientChart || canCreateTask;
  const clarityTooltips = chartClarityTooltips(autoUploadEmr);

  const chartActionsMenu = (
    <Menu>
      {autoUploadToEMR && (
        <Menu.Item
          key="sendToChart"
          disabled={!selectedRecord}
          onClick={() => {
            trackAction('send_to_chart', selectedCount, 'chart_dropdown');
            if (selectedRecord) onSendToChart(selectedRecord);
          }}
        >
          <Tooltip title={clarityTooltips.sendToChart || null} placement="right">
            <span>Send to Chart</span>
          </Tooltip>
        </Menu.Item>
      )}
      {createPatientChart && (
        <Menu.Item
          key="createChart"
          disabled={!selectedRecord}
          onClick={() => {
            trackAction('create_new_chart', selectedCount, 'chart_dropdown');
            if (selectedRecord) onCreateChart(selectedRecord);
          }}
        >
          Create New Chart
        </Menu.Item>
      )}
      {canCreateTask && (
        <Menu.Item
          key="createTask"
          disabled={!selectedRecord}
          onClick={() => {
            trackAction(autoUploadEmr === 'Kareo' ? 'create_note' : 'create_task', selectedCount, 'chart_dropdown');
            onCreateTask();
          }}
        >
          <Tooltip title={clarityTooltips.createTask || null} placement="right">
            <span>{autoUploadEmr === 'Kareo' ? 'Create Note' : 'Create Task'}</span>
          </Tooltip>
        </Menu.Item>
      )}
    </Menu>
  );

  const overflowMenu = (
    <Menu className="uploaded-records-overflow-menu">
      <Menu.Item
        key="createCopy"
        icon={<CopyOutlined />}
        disabled={!selectedRecord}
        onClick={() => {
          trackAction('create_copy', selectedCount, 'overflow');
          onCreateCopy();
        }}
      >
        Create Copy
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="mergeAndArchive"
        icon={<FolderOpenOutlined />}
        disabled={selectedCount <= 1}
        onClick={() => {
          trackAction('merge_and_archive', selectedCount, 'overflow');
          onMergeAndArchive();
        }}
      >
        Merge and Archive Records
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="referral-action-bar uploaded-records-action-bar">
      <h2 className="referral-action-bar__title">{title}</h2>
      {hasSelection && (
        <span className="referral-action-bar__count">
          {selectedCount}
          {' selected'}
        </span>
      )}
      {hasSelection && <div className="referral-action-bar__divider" />}

      <div className="referral-action-bar__actions">
        <Button
          type="primary"
          size="xsmall"
          label="Upload"
          leftIcon={<CloudArrowUp size={16} />}
          onClick={() => {
            trackAction('upload', selectedCount);
            onUpload();
          }}
        />

        {hasSelection && (
          <>
            <Button
              type="secondary"
              size="xsmall"
              label={selectedCount > 1 ? `Send (${selectedCount})` : 'Send'}
              leftIcon={<SendOutlined />}
              disabled={selectedCount > maxBulkSendRecords}
              onClick={() => {
                trackAction('send', selectedCount);
                onSend();
              }}
            />
            <Button
              type="secondary"
              size="xsmall"
              label="Edit"
              leftIcon={<EditOutlined />}
              disabled={!canEdit || selectedCount !== 1}
              onClick={() => {
                trackAction('edit', selectedCount);
                onEdit();
              }}
            />
            {hasChartActions && (
              <Dropdown overlay={chartActionsMenu} trigger={['click']}>
                <div>
                  <Button
                    type="secondary"
                    size="xsmall"
                    label="Chart Actions"
                    leftIcon={<ClipboardText size={16} weight="fill" />}
                    rightIcon={<CaretDown size={10} />}
                    disabled={!selectedRecord}
                    onClick={() => trackAction('chart_actions', selectedCount)}
                  />
                </div>
              </Dropdown>
            )}
            {referralManagement && !viewingArchivedRecords && (
              <Button
                type="secondary"
                size="xsmall"
                label="Move to Referral Management"
                leftIcon={<SwapOutlined />}
                disabled={!selectedRecord}
                onClick={() => {
                  trackAction('move_to_referral_management', selectedCount);
                  onMoveToReferralManagement();
                }}
              />
            )}
            <Button
              type="secondary"
              size="xsmall"
              label="Merge Records"
              leftIcon={<MergeCellsOutlined />}
              disabled={selectedCount <= 1}
              onClick={() => {
                trackAction('merge_records', selectedCount);
                onMerge();
              }}
            />
            <Button
              type="secondary"
              size="xsmall"
              label={viewingArchivedRecords ? 'Unarchive' : 'Archive'}
              leftIcon={<FolderOpenOutlined />}
              onClick={() => {
                const action = viewingArchivedRecords ? 'unarchive' : 'archive';
                trackAction(action, selectedCount);
                if (viewingArchivedRecords) onUnarchive();
                else onArchive();
              }}
            />
            <Dropdown
              overlay={overflowMenu}
              trigger={['click']}
              placement="bottomRight"
            >
              <button
                type="button"
                className="referral-action-bar__overflow-btn"
                aria-label="More upload actions"
                onClick={() => trackAction('overflow_menu_open', selectedCount, 'overflow')}
              >
                <DotsThree size={20} weight="bold" />
              </button>
            </Dropdown>
          </>
        )}
      </div>
    </div>
  );
};

UploadedActionBar.defaultProps = {
  selectedData: [],
  title: 'Uploads',
  autoUploadEmr: '',
};

UploadedActionBar.propTypes = {
  selectedData: PropTypes.array,
  title: PropTypes.string,
  viewingArchivedRecords: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  maxBulkSendRecords: PropTypes.number.isRequired,
  autoUploadToEMR: PropTypes.bool.isRequired,
  createPatientChart: PropTypes.bool.isRequired,
  canCreateTask: PropTypes.bool.isRequired,
  referralManagement: PropTypes.bool.isRequired,
  autoUploadEmr: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSendToChart: PropTypes.func.isRequired,
  onCreateChart: PropTypes.func.isRequired,
  onCreateTask: PropTypes.func.isRequired,
  onMoveToReferralManagement: PropTypes.func.isRequired,
  onMerge: PropTypes.func.isRequired,
  onMergeAndArchive: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onUnarchive: PropTypes.func.isRequired,
  onCreateCopy: PropTypes.func.isRequired,
};

export default UploadedActionBar;
